import React from 'react';
import styled from 'styled-components';

// Bento Card (glassmorphism style matching Profile)
const CardContainer = styled.div`
  background: ${props => props.theme.colors.cardBg};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 16px;
  padding: 14px;
  box-shadow: 
    0 4px 20px rgba(0, 0, 0, 0.06),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), 
              box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  
  /* Top shine line */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
    opacity: 0.5;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 
      0 8px 24px rgba(0, 0, 0, 0.1),
      inset 0 1px 0 rgba(255, 255, 255, 0.15);
  }
  
  @media (max-width: 600px) {
    padding: 10px;
    border-radius: 12px;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const Title = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.theme.colors.textPrimary};
  margin: 0;
  
  @media (max-width: 600px) {
    font-size: 13px;
  }
`;

const Action = styled.a`
  font-size: 11px;
  font-weight: 500;
  color: ${props => props.theme.colors.primary};
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

const Content = styled.div`
  flex: 1;
  min-height: 0;
`;

/**
 * BentoCard Component - Glassmorphism style card
 * 
 * @param {string} title - Card title
 * @param {string} action - Action link text (e.g., "View All")
 * @param {string} actionHref - Action link URL
 * @param {Function} onAction - Action click handler (alternative to actionHref)
 * @param {boolean} showHeader - Show header section (default: true if title provided)
 * @param {ReactNode} children - Card content
 * @param {string} className - Additional CSS class
 * @param {object} style - Inline styles
 */
const BentoCard = ({
  title,
  action,
  actionHref,
  onAction,
  showHeader,
  children,
  className,
  style
}) => {
  const shouldShowHeader = showHeader !== undefined ? showHeader : !!title;
  
  return (
    <CardContainer className={className} style={style}>
      {shouldShowHeader && (
        <Header>
          {title && <Title>{title}</Title>}
          {action && (
            <Action 
              href={actionHref} 
              onClick={onAction}
              as={actionHref ? 'a' : 'button'}
              style={!actionHref ? { background: 'none', border: 'none', cursor: 'pointer', padding: 0 } : undefined}
            >
              {action}
            </Action>
          )}
        </Header>
      )}
      <Content>
        {children}
      </Content>
    </CardContainer>
  );
};

export default BentoCard;
